import { useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import AiButton from '@components/AiButton';
import Button from '@components/Button';
import Overline from '@components/Overline';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { useLiveScoreValue } from './LiveScoreProvider';
import { AI_KIND, aiKindFor, draftedLabelKey, recommendationLabelKey } from './recommendations';
import { ASSIST_STATUS, useScoreAssistant } from './ScoreAssistantProvider';
import ScoreSummary from './ScoreSummary';
import SuggestionBlock from './SuggestionBlock';

const Card = styled('div')({
    backgroundColor: '#fff',
    border: '1px solid var(--divider)',
    borderRadius: 8,
});

const ScoreHead = styled('div')({
    padding: 16,
    borderBottom: '1px solid var(--divider)',
});

const List = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 8,
});

const ListTitle = styled(Overline)({
    padding: '6px 8px 8px',
    color: 'var(--text-tertiary)',
});

const Row = styled('div')({
    display: 'flex',
    alignItems: 'center',
    borderRadius: 8,
    paddingRight: 8,
    transition: 'background-color 120ms ease',
    '&:hover': {
        backgroundColor: 'var(--background-hover, rgba(31, 76, 92, 0.04))',
    },
});

const RowMain = styled('button')({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 44,
    padding: 8,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    font: 'inherit',
});

const RowAction = styled('span')({
    flexShrink: 0,
    '& .MuiButton-root': {
        height: 28,
        padding: '6px 10px',
        fontSize: 13,
    },
});

const DoneNote = styled(Typography)({
    display: 'block',
    margin: '-4px 8px 8px 40px',
    color: 'var(--text-secondary)',
});

const AllDone = styled('div')({
    display: 'grid',
    gridTemplateColumns: '20px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 12,
    padding: 16,
});

const labelSx = { flex: 1, minWidth: 0, color: 'var(--text-primary)' };
const circleSx = { width: 20, height: 20, flexShrink: 0, color: 'var(--text-tertiary)' };
const doneSx = { width: 20, height: 20, flexShrink: 0, color: 'var(--success-main)' };

export const ScoreRecommendations = () => {
    const { t } = useTranslation();
    const { recommendations } = useLiveScoreValue();
    const { assist, draftedFields, isLocked, request, undo, reveal } = useScoreAssistant();
    const countries = useSelector(state => state.countries?.data);
    const language = useSelector(state => state.auth.user?.language);
    const countryCode = useWatch({ name: 'country' });

    const country = countries?.find(item => item.iso2 === countryCode);
    const countryName = country?.translations?.[language?.toLowerCase()] || country?.name || countryCode;

    if (recommendations.length === 0 && draftedFields.length === 0) {
        return (
            <AllDone>
                <CheckCircleRoundedIcon sx={doneSx} />
                <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {t('ScorePanel.allDone')}
                </Typography>
            </AllDone>
        );
    }

    return (
        <List>
            <ListTitle variant="caption">{t('ScorePanel.recommendations')}</ListTitle>
            {draftedFields.map(field => (
                <div key={field}>
                    <Row>
                        <RowMain as="div" style={{ cursor: 'default' }}>
                            <CheckCircleRoundedIcon sx={doneSx} />
                            <Typography variant="body2" sx={labelSx}>
                                {t(draftedLabelKey(field))}
                            </Typography>
                        </RowMain>
                        <RowAction>
                            <Button
                                variant="text"
                                startIcon={<UndoRoundedIcon />}
                                disabled={isLocked}
                                onClick={() => undo(field)}
                            >
                                {t('ScorePanel.undo')}
                            </Button>
                        </RowAction>
                    </Row>
                    <DoneNote variant="caption">{t('ScorePanel.reviewBeforeSave')}</DoneNote>
                </div>
            ))}
            {recommendations.map(recommendation => {
                const { key } = recommendation;
                const kind = recommendation.withoutAi ? null : aiKindFor(key);
                const isLoading = assist[key]?.status === ASSIST_STATUS.LOADING;
                return (
                    <div key={key}>
                        <Row>
                            <RowMain type="button" onClick={() => reveal(recommendation)}>
                                <RadioButtonUncheckedRoundedIcon sx={circleSx} />
                                <Typography variant="body2" sx={labelSx}>
                                    {t(recommendationLabelKey(recommendation))}
                                </Typography>
                                {!kind && (
                                    <ChevronRightRoundedIcon
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            flexShrink: 0,
                                            color: 'var(--text-secondary)',
                                        }}
                                    />
                                )}
                            </RowMain>
                            {kind && (
                                <RowAction>
                                    <AiButton
                                        disabled={isLocked || isLoading || !!assist[key]}
                                        onClick={() => request(key)}
                                    >
                                        {t(kind === AI_KIND.DESCRIPTION ? 'ScorePanel.write' : 'ScorePanel.suggest')}
                                    </AiButton>
                                </RowAction>
                            )}
                        </Row>
                        <SuggestionBlock recommendationKey={key} country={countryName} />
                    </div>
                );
            })}
        </List>
    );
};

const ScoreWidget = () => (
    <Card>
        <ScoreHead>
            <ScoreSummary />
        </ScoreHead>
        <ScoreRecommendations />
    </Card>
);

export default ScoreWidget;
